import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AppInjector } from '@app/app.injector';
import {
  stringCaseInsensitiveComparator
} from '@app/shared/common/string-case-insensitive.comparator/string-case-insensitive.comparator';
import { Subject } from '@app/shared/rx/rx-wrapper';
import {
  ButtonDefinition,
  ListGridEvent,
  XuxGridAction,
  XUX_GRID_ACTIONS,
} from '@app/shared/types';
import { getDisplayedItemsIds, mergeWhenNil } from '@app/shared/utils';
import {
  GridApi,
  GridOptions,
  GridReadyEvent,
} from 'ag-grid-community';
import {
  camelCase,
  capitalize,
  chain,
  filter,
  forEach,
  get,
  isEmpty,
  isNil,
  map,
  reduce,
  startCase,
  toLower,
  values,
} from 'lodash';
import { BaseComponent } from '../base-component';
import { DMColDef } from '../dm-grid/dm-grid.component';
import { FilterPanelUpdates, IFilter } from '../filter-panel/filter.types';
import { ListGridConfig, ListGridTips } from './list-grid.component.types';
import { columnIncludeFilter, CustomFilterComponent } from '../grid-filters';
import { getGridFinalValue } from './grid-utils';
import { I18n } from '@app/i18n/strings-keys';

@Component({
  selector: 'xea-list-grid',
  templateUrl: './list-grid.component.html',
  styleUrls: ['./list-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ListGridComponent
  extends BaseComponent
  implements OnInit, OnChanges
{
  @Input()
  public config: ListGridConfig = null;

  @Input()
  public gridData: object[];

  @HostBinding('attr.e2e-attr')
  public e2eAttr: string;

  public title: string;

  @Input()
  public showLoader: boolean = true;

  @Input()
  public showFilters: boolean = false;

  @Input()
  public filters: IFilter[];

  @Input()
  public gridOptionConfig: Partial<GridOptions> = {};

  @Input()
  public customTips: ListGridTips = {};

  @Input()
  public footerButtons: ButtonDefinition[] = [];

  @Output()
  public action: EventEmitter<ListGridEvent> =
    new EventEmitter<ListGridEvent>();

  public addBtn: ButtonDefinition;
  public refreshBtn: ButtonDefinition;

  public mainTitle: string;
  public subTitle: string;
  public itemsCount: number = 0;
  public showingItemsText: string = I18n.TransferStrings.ShowingItems;
  public gridTips: ListGridTips = {};

  public get showingItemsParams() {
    return { count: this.itemsCount ?? 0 };
  }

  public columnDefs: DMColDef[];
  public userGridOptions: GridOptions;

  public gridOptions: GridOptions = {
    defaultColDef: <DMColDef>{
      suppressMovable: true,
    },
  };

  public defaultFilters: IFilter[] = [];

  public gridReady$: Subject<GridReadyEvent> = new Subject();
  public gridApi: GridApi;
  public noRowsTemplate: string = I18n.CommonMessages.NoRowsToShow;

  public get tips(): string {
    return chain(values(this.gridTips))
      .orderBy(
        (tip) => (isNil(tip?.order) ? Number.MAX_SAFE_INTEGER : tip?.order),
        'asc',
      )
      .filter((tip) => !tip?.hidden)
      .map((tip) => tip?.message)
      .filter(Boolean)
      .join(' | ')
      .value();
  }

  /**
   * need make sure route is inject in constructor to get right route data
   * @param route
   */
  public constructor(public route: ActivatedRoute) {
    super(AppInjector.injector);
  }

  public ngOnInit() {
    const config = this.config ?? (this.route.snapshot.data as ListGridConfig);
    this.e2eAttr = get(config, 'pageE2EAttr', '');
    this.title = capitalize(camelCase(this.e2eAttr));
    this.mainTitle = get(config, 'mainTitle', '');
    this.subTitle = get(config, 'subTitle', '');
    this.columnDefs = this.combineColumnDefsWithDefault(
      get(config, 'columnDefs', []),
    );

    this.addBtn = get(config, ['header', 'addBtn']);
    this.refreshBtn = get(config, ['header', 'refreshBtn']);
    this.updateGridTips(config);

    this.gridOptions = this.createGridOptions();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('gridData' in changes) {
      this.itemsCount = getDisplayedItemsIds(this.gridApi)?.length ?? 0;
    }

    if ('config' in changes) {
      const newConfig = changes.config.currentValue;
      this.mainTitle = get(newConfig, 'mainTitle', '');
      this.subTitle = get(newConfig, 'subTitle', '');
    }

    if ('gridTips' in changes) {
      this.updateGridTips(
        changes.config?.currentValue ?? this.config ?? this.route.snapshot.data,
      );
    }
  }

  public updateGridTips(config: ListGridConfig): void {
    this.gridTips = {
      ...this.customTips,
      maxSelectableRowsError: {
        maxLength: get(config, 'maxSelectableRows', null),
        message: '',
      },
    };
  }

  public buildDisplayTitle(): string {
    const isMainTitleValid = !isEmpty(this.mainTitle);
    const isSubTitleValid = !isEmpty(this.subTitle);

    if (isMainTitleValid && isSubTitleValid) {
      return `${this.mainTitle} - ${this.i18nService.instant(this.subTitle)}`;
    }

    if (isMainTitleValid || isSubTitleValid) {
      return `${this.mainTitle ?? ''}${this.subTitle ? this.i18nService.instant(this.subTitle) : ''}`;
    }
  }

  public combineColumnDefsWithDefault(columnDefs: DMColDef[]): DMColDef[] {
    forEach(columnDefs, (column) => {
      // Translate column header if it's an i18n key
      if (column.headerName) {
        column.headerName = this.i18nService.instant(column.headerName);
      }

      if (column.filter === CustomFilterComponent || column.filter === true) {
        // set showFilters to true when has at least one column is filter column
        this.showFilters = true;

        if (column.filter === true) {
          column.filter = CustomFilterComponent;
        }

        const defaultFilterParams = {
          customFilter: columnIncludeFilter,
          defaultSelected: true,
        };

        column.filterParams = column.filterParams ?? {};

        mergeWhenNil(column.filterParams, defaultFilterParams);

        const defaultHeaderCompParams = {
          template:
            '<div class="ag-cell-label-container" role="presentation">' +
            '  <div ref="eLabel" class="ag-header-cell-label" role="presentation">' +
            '    <span ref="eSortOrder" class="ag-header-icon ag-sort-order"></span>' +
            '    <span ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon"></span>' +
            '    <span ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon"></span>' +
            '    <span ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>' +
            '    <span ref="eText" class="ag-header-cell-text" role="columnheader"></span>' +
            '  </div>' +
            '</div>',
        };

        column.headerComponentParams = column.headerComponentParams ?? {};

        mergeWhenNil(column.headerComponentParams, defaultHeaderCompParams);
      }
    });

    return columnDefs;
  }

  public buildDefaultFilters(): IFilter[] {
    if (isEmpty(this.gridData)) {
      return [];
    }

    return map(
      filter(this.columnDefs, { filter: CustomFilterComponent }),
      (column) => {
        const field = column.field;

        return {
          id: field,
          name: () => column.headerName || startCase(field),
          items: chain(this.gridData)
            .map((data) => {
              const { cellValue, formattedValue } = getGridFinalValue(
                data,
                column,
              );

              return {
                id: cellValue,
                name: () => formattedValue,
                selected: column.filterParams?.defaultSelected ?? false,
              };
            })
            .uniqBy('id')
            .sortBy((data) => toLower(data.name()))
            .value(),
        };
      },
    );
  }

  public createGridOptions(): GridOptions {
    return {
      rowSelection: 'single',
      suppressColumnVirtualisation: true,
      defaultColDef: <DMColDef>{
        sortable: true,
        resizable: false,
        suppressMovable: true,
        comparator: stringCaseInsensitiveComparator,
      },
      rowClassRules: {
        'no-selected-bg': () => true,
      },
      onGridReady: (event: GridReadyEvent) => {
        this.gridApi = event.api;
        this.gridReady$.next(event);
        this.handleAction({
          type: XUX_GRID_ACTIONS.GRID_READY,
          payload: {},
        });
      },
      onFirstDataRendered: () => {
        this.gridApi?.sizeColumnsToFit();
      },
      onFilterChanged: () => {
        // use this to update items count after filter applied
        this.itemsCount = getDisplayedItemsIds(this.gridApi)?.length ?? 0;
      },
      onRowDataUpdated: () => {
        if (isNil(this.filters) && this.showFilters) {
          this.defaultFilters = this.buildDefaultFilters();
        }
      },
      ...this.gridOptionConfig,
      onSelectionChanged: (event) => {
        const selectedRowCount = this.gridApi.getSelectedNodes().length;

        if (
          this.gridTips?.maxSelectableRowsError?.maxLength &&
          selectedRowCount > this.gridTips.maxSelectableRowsError.maxLength
        ) {
          this.gridTips.maxSelectableRowsError.message =
            this.i18nService.instant(
              I18n.CommonMessages.MaxSelectableRowsError,
              {
                maxLength: this.gridTips.maxSelectableRowsError.maxLength,
              },
            );
        } else {
          this.gridTips.maxSelectableRowsError.message = '';
        }
        this.gridOptionConfig.onSelectionChanged?.(event);
      },
    };
  }

  public onSearchChange(value: string): void {
    // update value to search box
    this.gridApi.setGridOption('quickFilterText', value);

    // emit quick filter action
    const gridAction: XuxGridAction = {
      type: XUX_GRID_ACTIONS.QUICK_FILTER_CHANGED,
      payload: { value },
    };

    this.handleAction(gridAction);

    this.cd.markForCheck();
  }

  public onApplyFilters(filterUpdates: FilterPanelUpdates): void {
    // emit filter action
    const gridAction: XuxGridAction = {
      type: XUX_GRID_ACTIONS.FILTER_CHANGES,
      payload: { filters: filterUpdates.payload },
    };

    this.handleAction(gridAction);
  }

  public handleAction(
    gridAction: XuxGridAction,
    api: {
      gridApi: GridApi;
    } = {
      gridApi: this.gridApi,
    },
  ): void {
    const actionHandlerMapping: {
      [action in XUX_GRID_ACTIONS]?: (action: XuxGridAction) => void;
    } = {
      [XUX_GRID_ACTIONS.QUICK_FILTER_CHANGED]: () => {
        this.itemsCount = getDisplayedItemsIds(this.gridApi)?.length ?? 0;
      },
      [XUX_GRID_ACTIONS.FILTER_CHANGES]: () => {
        if (this.gridApi) {
          const filterModel = reduce(
            gridAction.payload.filters,
            (model, curFilter) => {
              if (!isEmpty(curFilter.selected)) {
                model[curFilter.id] = {
                  type: 'includes',
                  filter: curFilter.selected,
                };
              }

              return model;
            },
            {},
          );

          this.gridApi.setFilterModel(filterModel);
        }
      },
    };

    const actionHandler = get(actionHandlerMapping, gridAction.type, () => {});

    actionHandler(gridAction);

    this.action.emit({
      api,
      action: gridAction,
    });
  }
}
